import { AuthorizeUserByRequestArgs, isUserAuthorizedToViewPage } from '../../utils/user'
import { useAuthState } from '../../hooks/useAuthState'
import { useEffect, useState } from 'react'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import { useRouter } from 'next/router'

export interface AuthorizationState {
  isAuthorized: boolean
  isAuthorizationChecked: boolean
  isAuthenticated: boolean
}

type SelectedAuthorizeUserByRequestArgs =
  | 'unauthorizedOrgRoles'
  | 'nameOfAuthorizedOrg'
  | 'authorizedOperation'

interface AuthorizationStateParams
  extends Pick<AuthorizeUserByRequestArgs, SelectedAuthorizeUserByRequestArgs> {
  /**
   * Redirect destination if user is unauthorized. Omit if you'd like to display a message instead using the isAuthorized flag.
   */
  unauthorizedRedirectTo?: string
  isExternalRedirect?: boolean
}

export const useAuthorizationState = ({
  unauthorizedRedirectTo,
  unauthorizedOrgRoles,
  nameOfAuthorizedOrg,
  authorizedOperation,
  isExternalRedirect
}: AuthorizationStateParams): AuthorizationState => {
  const router = useRouter()
  const isMounted = useIsMounted()
  const { isAuthenticated, jwt } = useAuthState()
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [isAuthorizationChecked, setIsAuthorizationChecked] = useState(false)

  useEffect(() => {
    if (isAuthenticated && !isAuthorizationChecked && jwt) {
      const isAuthorized = isUserAuthorizedToViewPage({
        unauthorizedOrgRoles,
        authorizedOperation,
        nameOfAuthorizedOrg,
        jwt
      })

      if (!isAuthorized && unauthorizedRedirectTo) {
        if (isExternalRedirect) window.location.href = unauthorizedRedirectTo
        else router.push(unauthorizedRedirectTo)
      }

      if (isMounted()) {
        setIsAuthorized(isAuthorized)
        setIsAuthorizationChecked(true)
      }
    }
  }, [
    isAuthenticated,
    isAuthorizationChecked,
    router,
    isMounted,
    unauthorizedRedirectTo,
    unauthorizedOrgRoles,
    nameOfAuthorizedOrg,
    authorizedOperation,
    jwt,
    isExternalRedirect
  ])

  return {
    isAuthorizationChecked,
    isAuthorized,
    isAuthenticated
  }
}
